import React, { memo, useEffect, useRef, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { Checkbox, Label } from "flowbite-react";
import { FieldLabel } from "../../../../atoms/FieldLabel";
import { useTranslation } from "react-i18next";
import useAuth from "../../../../../hooks/useAuth";
import { PlanTier, usePermissions } from "../../../../../utils/permissions";
import { UnlockBadge } from "../../../../atoms/UnlockBadge";
import { PlansModal } from "../../../../modals/PlansModal";
import FeatureUpgradePrompt from "../../../../atoms/FeatureUpgradePrompt";
import { HiShieldCheck } from "react-icons/hi";

type Props = {
  control: any;
  name: string;
  label: string;
  className?: string;
  supportsVerification?: boolean;
};

export const FieldToggleControl: React.FC<Props> = memo(
  ({ control, name, label, className, supportsVerification = false }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const permissions = usePermissions(user);
    const visibleFieldName = `${name}.visible`;
    const requiredFieldName = `${name}.required`;
    const verificationFieldName = `${name}.requireVerification`;

    const isVisible = useWatch({ control, name: visibleFieldName });

    const requireVerification = useWatch({
      control,
      name: verificationFieldName,
    });

    const hasPremiumAccess = permissions.isAtLeastTier(PlanTier.PREMIUM);

    const [
      showUpgradePhoneValidationModal,
      setShowUpgradePhoneValidationModal,
    ] = useState(false);

    const requiredFieldOnChange = useRef<((value: boolean) => void) | null>(
      null
    );

    const verificationFieldOnChange = useRef<((value: boolean) => void) | null>(
      null
    );

    useEffect(() => {
      if (
        supportsVerification &&
        requireVerification &&
        !hasPremiumAccess &&
        verificationFieldOnChange.current
      ) {
        verificationFieldOnChange.current(false);
      }
    }, [supportsVerification, requireVerification, hasPremiumAccess]);

    return (
      <div
        className={`grid grid-cols-1 gap-3 px-3 py-3 bg-white rounded border border-gray-200 hover:border-blue-300 transition-colors ${
          className ?? ""
        }`}
      >
        <FieldLabel className="mb-0">{label}</FieldLabel>

        <div className="flex flex-wrap gap-4 md:gap-6">
          <Controller
            name={visibleFieldName}
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-[auto_1fr] items-center gap-2 w-[120px]">
                <Checkbox
                  id={`${name.replace(/\./g, "_")}_visible`}
                  checked={!!field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    if (!e.target.checked) {
                      if (requiredFieldOnChange.current) {
                        requiredFieldOnChange.current(false);
                      }
                      if (
                        supportsVerification &&
                        verificationFieldOnChange.current
                      ) {
                        verificationFieldOnChange.current(false);
                      }
                    }
                  }}
                />
                <Label
                  htmlFor={`${name.replace(/\./g, "_")}_visible`}
                  className="text-gray-600 text-sm cursor-pointer whitespace-nowrap"
                >
                  {t("customRegistration.coreFields.visible")}
                </Label>
              </div>
            )}
          />

          <Controller
            name={requiredFieldName}
            control={control}
            render={({ field }) => {
              requiredFieldOnChange.current = field.onChange;

              return (
                <div className="grid grid-cols-[auto_1fr] items-center gap-2 w-[120px]">
                  <Checkbox
                    id={`${name.replace(/\./g, "_")}_required`}
                    checked={!!field.value}
                    disabled={!isVisible}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (
                        !e.target.checked &&
                        supportsVerification &&
                        verificationFieldOnChange.current
                      ) {
                        verificationFieldOnChange.current(false);
                      }
                    }}
                    className={!isVisible ? "cursor-not-allowed" : ""}
                  />
                  <Label
                    htmlFor={`${name.replace(/\./g, "_")}_required`}
                    className={`text-gray-600 text-sm whitespace-nowrap ${
                      !isVisible
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    }`}
                  >
                    {t("customRegistration.coreFields.required")}
                  </Label>
                </div>
              );
            }}
          />

          {supportsVerification && (
            <div className="w-full md:w-auto">
              <Controller
                name={verificationFieldName}
                control={control}
                render={({ field }) => {
                  verificationFieldOnChange.current = field.onChange;

                  const isDisabled = !isVisible || !hasPremiumAccess;

                  return (
                    <div className="flex items-center gap-2">
                      <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                        <Checkbox
                          id={`${name.replace(/\./g, "_")}_verification`}
                          checked={!!field.value}
                          disabled={isDisabled}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            if (
                              e.target.checked &&
                              requiredFieldOnChange.current
                            ) {
                              requiredFieldOnChange.current(true);
                            }
                          }}
                          className={isDisabled ? "cursor-not-allowed" : ""}
                        />
                        <Label
                          htmlFor={`${name.replace(/\./g, "_")}_verification`}
                          className={`text-gray-600 text-sm whitespace-nowrap ${
                            isDisabled
                              ? "cursor-not-allowed opacity-50"
                              : "cursor-pointer"
                          }`}
                        >
                          {t(
                            "customRegistration.coreFields.requireVerification"
                          )}
                        </Label>
                      </div>
                      {!hasPremiumAccess && (
                        <UnlockBadge className="flex-shrink-0" />
                      )}
                    </div>
                  );
                }}
              />
            </div>
          )}
        </div>

        {supportsVerification && !hasPremiumAccess && isVisible && (
          <FeatureUpgradePrompt
            featureName={
              t(
                "customRegistration.phoneVerification.upgradeBlock.title"
              ) as string
            }
            icon={HiShieldCheck}
            featureDescription={
              t(
                "customRegistration.phoneVerification.upgradeBlock.description"
              ) as string
            }
            benefits={[
              t(
                "customRegistration.phoneVerification.upgradeBlock.benefit1"
              ) as string,
              t(
                "customRegistration.phoneVerification.upgradeBlock.benefit2"
              ) as string,
              t(
                "customRegistration.phoneVerification.upgradeBlock.benefit3"
              ) as string,
            ]}
            onUpgrade={() => setShowUpgradePhoneValidationModal(true)}
            className="mb-0"
          />
        )}

        <PlansModal
          showPlansModal={showUpgradePhoneValidationModal}
          targetPlan={PlanTier.PREMIUM}
          setShowPlansModal={setShowUpgradePhoneValidationModal}
        />
      </div>
    );
  }
);
